import * as React from 'react';

export interface ResizableDividerProps {
  /** Direction of resize: vertical divider resizes horizontally, horizontal divider resizes vertically */
  direction: 'vertical' | 'horizontal';

  /** Callback when resize occurs. Omit or pass undefined to disable dragging. */
  onResize?: (delta: number) => void;

  /** Callback fired once when the drag starts (mousedown) */
  onResizeStart?: () => void;

  /** Callback fired once when the drag ends (mouseup) */
  onResizeEnd?: () => void;

  /** Optional className for styling */
  className?: string;

  /** Aria label for accessibility */
  ariaLabel?: string;

  /**
   * When true, disables hover highlighting and drag interaction.
   * Use when the adjacent pane is collapsed and resizing is meaningless.
   */
  disabled?: boolean;

  /**
   * When provided, renders a collapse chevron button centered on the divider.
   * Shown on hover; clicking it calls this callback instead of starting a drag.
   */
  onCollapse?: () => void;
}

/**
 * ResizableDivider - draggable divider for resizing panes
 *
 * Visual: 1px line with an 8px transparent hit area.
 * On hover/drag the full hit area highlights with the accent color.
 *
 * - vertical divider: runs top-to-bottom, resizes left/right panes
 * - horizontal divider: runs left-to-right, resizes top/bottom panes
 *
 * Follows Protoss architecture:
 * - no inline styles
 * - styled via CSS class
 * - accessible keyboard interaction
 */
export function ResizableDivider({
  direction,
  onResize,
  onResizeStart,
  onResizeEnd,
  className = '',
  ariaLabel = 'Resize pane',
  disabled = false,
  onCollapse,
}: ResizableDividerProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const startPosRef = React.useRef(0);
  const onResizeRef = React.useRef(onResize);
  const onResizeEndRef = React.useRef(onResizeEnd);
  React.useLayoutEffect(() => {
    onResizeRef.current = onResize;
    onResizeEndRef.current = onResizeEnd;
  });

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent) => {
      if (!onResizeRef.current) return;
      e.preventDefault();
      setIsDragging(true);
      onResizeStart?.();
      startPosRef.current = direction === 'vertical' ? e.clientX : e.clientY;

      document.body.style.userSelect = 'none';
      document.body.style.cursor = direction === 'vertical' ? 'col-resize' : 'row-resize';
    },
    [direction, onResizeStart]
  );

  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const currentPos = direction === 'vertical' ? e.clientX : e.clientY;
      const delta = currentPos - startPosRef.current;
      startPosRef.current = currentPos;
      onResizeRef.current?.(delta);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      onResizeEndRef.current?.();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, direction]);

  const baseClass =
    direction === 'vertical' ? 'resizable-divider-vertical' : 'resizable-divider-horizontal';

  const activeClass = isDragging ? `${baseClass}--active` : '';
  const disabledClass = disabled ? `${baseClass}--disabled` : '';

  return (
    <div
      role="separator"
      aria-label={ariaLabel}
      aria-orientation={direction}
      aria-disabled={disabled || undefined}
      className={[baseClass, activeClass, disabledClass, className].filter(Boolean).join(' ')}
      onMouseDown={handleMouseDown}
      data-dragging={isDragging || undefined}
    >
      {onCollapse && !disabled && (
        <button
          type="button"
          className="resizable-divider__collapse-btn"
          aria-label="Collapse pane"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onCollapse}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M10 12l-4-4 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
